<script setup>
import ResultForm from '../components/ResultForm.vue'
import { onMounted, ref } from 'vue'
import { useHead } from '@vueuse/head'
import { useSystemConfig } from '@/composables/useSystemConfig'

const isInitialLoading = ref(true)
const { config, fetchConfig } = useSystemConfig()

onMounted(async () => {
  await fetchConfig()
  // Add a minimum loading time for better UX
  setTimeout(() => {
    isInitialLoading.value = false
  }, 2000)
})

// SEO Meta tags
useHead({
  title: 'نتيجة القبول للجامعات السودانية - وزارة التعليم العالي والبحث العلمي',
  meta: [
    {
      name: 'description',
      content: 'استعلم عن نتائج القبول للجامعات السودانية عبر وزارة التعليم العالي والبحث العلمي بالتعاون مع MTN السودان. أدخل رقم الجلوس ورقم الهاتف للحصول على النتيجة فوراً'
    },
    {
      property: 'og:title',
      content: 'نتيجة القبول للجامعات السودانية - وزارة التعليم العالي والبحث العلمي'
    },
    {
      property: 'og:description',
      content: 'استعلم عن نتائج القبول للجامعات السودانية عبر وزارة التعليم العالي والبحث العلمي بالتعاون مع MTN السودان'
    },
    {
      name: 'robots',
      content: 'noindex, nofollow'
    }
  ]
})
</script>

<template>
  <!-- Loading Screen -->
  <div v-if="isInitialLoading" class="loading-screen">
    <div class="loading-container">
      <!-- Animated Logos -->
      <!-- <div class="logos-loading">
        <div class="logo-wrapper">
          <img src="../assets/logo.png" alt="Ministry Logo" class="loading-logo ministry-loading" />
        </div>
        <div class="logo-wrapper">
          <img src="../assets/logo-mtn.png" alt="MTN Logo" class="loading-logo mtn-loading" />
        </div>
      </div> -->
      
      <!-- Loading Animation -->
      <div class="loading-animation">
        <div class="spinner-container">
          <div class="spinner"></div>
          <div class="spinner-ring"></div>
        </div>
        <!-- <h2 class="loading-text">جاري التحميل...</h2> -->
        <p class="loading-subtitle">نتيجة القبول للجامعات السودانية من MTN</p>
      </div>
      
      <!-- Progress Bar -->
      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress-fill"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div v-else class="bg-gradient-sudan d-flex align-items-center justify-content-center .via-primary to-secondary">
    <div class="container mx-auto">
      <div class="d-flex justify-content-center align-items-center gap-3 mb-4">
        <!-- <img src="../assets/logo.png" alt="Logo" class="img-fluid rounded-3 bg-warning p-2 h-24"
          style="height: 8rem;" /> -->
        <img src="../assets/logo-mtn.png" alt="MTN Logo" class="img-fluid rounded-3 bg-warning p-2 h-24"
          style="height: 6rem;" />
      </div>
      <div class="text-center text-color mb-5" style="font-family: 'MTNBrighterSans', sans-serif;">
        <h3 class="fs-5">{{ config?.title_ar || 'نتيجة القبول العام للجامعات السودانية' }}</h3>
        <!-- <h4 class="fs-6">{{ config?.year || 'للعام 2023 المؤجل' }}</h4> -->
      </div>
      <ResultForm />
    </div>
  </div>
</template>

<style>
/* Loading Screen Styles */
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, #FFC403 0%, #FFC403 50%, #000000 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  font-family: 'MTNBrighterSans', sans-serif;
}

.loading-container {
  text-align: center;
  max-width: 400px;
  padding: 2rem;
}

.logos-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  margin-bottom: 3rem;
}

.logo-wrapper {
  position: relative;
}

.loading-logo {
  width: 120px;
  height: 120px;
  object-fit: contain;
  border-radius: 20px;
  background: white;
  padding: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: logoFloat 3s ease-in-out infinite;
}

.ministry-loading {
  animation-delay: 0s;
}

.mtn-loading {
  animation-delay: 0.5s;
}

@keyframes logoFloat {
  0%, 100% {
    transform: translateY(0px) scale(1);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
  50% {
    transform: translateY(-20px) scale(1.05);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
}

.loading-animation {
  margin-bottom: 3rem;
}

.spinner-container {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;
}

.spinner {
  position: absolute;
  width: 80px;
  height: 80px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-ring {
  position: absolute;
  width: 60px;
  height: 60px;
  top: 10px;
  left: 10px;
  border: 3px solid rgba(0, 0, 0, 0.3);
  border-bottom: 3px solid #FFC403;
  border-radius: 50%;
  animation: spin 1.5s linear infinite reverse;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 2rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 0.5rem;
  animation: textPulse 2s ease-in-out infinite;
}

.loading-subtitle {
  font-size: 1.1rem;
  color: rgba(0, 0, 0, 0.8);
  margin-bottom: 0;
  animation: textPulse 2s ease-in-out infinite;
  animation-delay: 0.5s;
}

@keyframes textPulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
}

.progress-container {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #000, #FFC403, #000);
  border-radius: 10px;
  animation: progressAnimation 1.5s ease-in-out infinite;
}

@keyframes progressAnimation {
  0% {
    width: 0%;
    transform: translateX(-100%);
  }
  50% {
    width: 100%;
    transform: translateX(0%);
  }
  100% {
    width: 100%;
    transform: translateX(100%);
  }
}

/* Responsive Design for Loading */
@media (max-width: 768px) {
  .loading-container {
    padding: 1rem;
  }
  
  .logos-loading {
    gap: 2rem;
    margin-bottom: 2rem;
  }
  
  .loading-logo {
    width: 80px;
    height: 80px;
    padding: 0.5rem;
  }
  
  .spinner-container {
    width: 60px;
    height: 60px;
  }
  
  .spinner {
    width: 60px;
    height: 60px;
  }
  
  .spinner-ring {
    width: 40px;
    height: 40px;
    top: 10px;
    left: 10px;
  }
  
  .loading-text {
    font-size: 1.5rem;
  }
  
  .loading-subtitle {
    font-size: 1rem;
  }
}

/* @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');

.font-arabic {
  font-family: 'MTNBrighterSans', sans-serif;
} */

.h-24 {
  height: 8rem;
}

.bg-gradient-sudan {
  background-image: linear-gradient(to bottom right, #FFC403 0%, #FFC403 50%, #000000 100%);
  min-height: 100vh;
}

.text-color {
  opacity: 1;
  color: rgba(0, 0, 0, 1) !important;
}
</style>